import { useMemo } from 'react';
import { useLoaderData, useLocation, useSearchParams } from 'react-router';
import { useTracker } from '@archipro-website/tracker';

import { useProfessionalInitialRouteData } from './use-professional-initial-route-data';
import {
    buildLocationRevealExtraData,
    getInitialContactID,
    getLinkedProfileLocations,
} from '../util/contact-page-helper';
import type {
    ContactLoaderData,
    LocationStateType,
} from '../type/contact-page-types';

const NO_LINKED_PROFILES: NonNullable<ContactLoaderData['linkedProfiles']> = {
    linkedProfilesCount: 0,
    linkedProfilesList: [],
    linkedProfilesProductSharing: [],
};

/**
 * `?expandAddress=true` (with an optional `?ID=`) arrives on the Contact tab
 * with one address already revealed — the requested location, else the first.
 * Counts as that location's `LocationReveal`, logged once per session.
 *
 * Writes the reveal straight to sessionStorage during render, before the
 * address reveals mount, so they read it as revealed on their first render.
 * Uses the same key and sub-key as those reveals: the location's own profile
 * and the location ID.
 */
export const useExpandAddressReveal = () => {
    const tracker = useTracker();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const professional = useProfessionalInitialRouteData();
    const loaderData = useLoaderData<ContactLoaderData>();
    const linkedProfiles = loaderData?.linkedProfiles ?? NO_LINKED_PROFILES;

    useMemo(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (searchParams.get('expandAddress') !== 'true' || !professional) {
            return;
        }

        const locations = getLinkedProfileLocations(
            professional,
            linkedProfiles,
        );
        const requestedID = getInitialContactID(
            (location.state as LocationStateType) ?? ({} as LocationStateType),
            searchParams,
        );
        const locationToReveal =
            locations.find((loc) => loc.ID === requestedID) ?? locations[0];
        if (!locationToReveal) {
            return;
        }

        const ownerID = locationToReveal.professionalID ?? professional.ID;
        const sessionKey = `reveal-Location-professional-${ownerID}`;
        const subKey = locationToReveal.ID.toString();
        const revealedSubKeys: string[] = JSON.parse(
            sessionStorage.getItem(sessionKey) || '[]',
        );
        if (revealedSubKeys.includes(subKey)) {
            return;
        }

        sessionStorage.setItem(
            sessionKey,
            JSON.stringify([...revealedSubKeys, subKey]),
        );
        tracker.log('LocationReveal', {
            url: new URL(window.location.href),
            professionalId: professional.ID,
            data: {
                ProfessionalID: professional.ID,
                ExtraData: buildLocationRevealExtraData(locationToReveal),
            },
        });
        // Runs once per arrival: the query string and the page's professional
        // are what identify one.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, professional?.ID]);
};
